import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarCheck,
  FileWarning,
  History,
  Loader2,
  PackageCheck,
  RotateCcw,
  Search,
  Undo2,
} from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { formatarData } from '@/lib/utils';
import { useAuth } from '@/features/auth/contexto';
import type { SolicitacaoDetalhe } from '@/features/solicitacoes/tipos';
import type { StatusSolicitacao } from '@/features/solicitacoes/status';
import { ehDemandaReprimida } from '@/features/solicitacoes/origem';
import { ehResponsavelDaUbs, nomesDosResponsaveis } from '@/features/solicitacoes/responsaveis';
import type { RespostaConsultaEsus, SituacaoEsus } from '@/features/regulacao/tipos';
import {
  ROTULOS_TIPO,
  type LocalAtivo,
  type ProcedimentoAtivo,
  type ProfissionalAtivo,
} from '@/features/admin/tipos';
import { ConferenciaEsus } from './conferencia-esus';
import { Botao } from '@/components/ui/button';
import { AreaTexto, Entrada } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { Campo } from '@/components/ui/campo';
import {
  Cartao,
  CartaoCabecalho,
  CartaoConteudo,
  CartaoDescricao,
  CartaoTitulo,
} from '@/components/ui/card';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

type Acao = 'pendencia' | 'documentacao' | 'devolver' | 'agendar' | 'reabrir' | 'anterior';

const TERMINAIS: StatusSolicitacao[] = ['AGENDADO', 'DEVOLVIDO'];

interface PainelDecisaoProps {
  solicitacao: SolicitacaoDetalhe;
}

/**
 * Painel de decisão da Regulação.
 *
 * Só aparece para os reguladores **responsáveis pela UBS** de origem. Os demais
 * reguladores enxergam a solicitação inteira, mas sem as ações — e com um
 * aviso explicando por quê, para não parecer defeito da tela.
 */
export function PainelDecisao({ solicitacao }: PainelDecisaoProps) {
  const { usuario } = useAuth();
  const queryClient = useQueryClient();
  const [acao, setAcao] = React.useState<Acao | null>(null);

  const { data: situacaoEsus } = useQuery({
    queryKey: ['esus-situacao'],
    queryFn: () => api.get<SituacaoEsus>('/esus/situacao'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (usuario?.perfil !== 'REGULACAO') return null;

  const ehResponsavel = ehResponsavelDaUbs(solicitacao.ubs, usuario.id);
  const semResponsavel = solicitacao.ubs.reguladores.length === 0;
  const concluida = TERMINAIS.includes(solicitacao.status);

  const invalidar = async () => {
    await queryClient.invalidateQueries({ queryKey: ['solicitacao', solicitacao.id] });
    await queryClient.invalidateQueries({ queryKey: ['historico', solicitacao.id] });
    await queryClient.invalidateQueries({ queryKey: ['solicitacoes'] });
    await queryClient.invalidateQueries({ queryKey: ['estatisticas'] });
  };

  if (semResponsavel || !ehResponsavel) {
    return (
      <Cartao>
        <CartaoConteudo className="p-4">
          <p className="text-silenciado-texto flex items-start gap-2 text-sm">
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              {semResponsavel ? (
                <>
                  A UBS <strong>{solicitacao.ubs.nome}</strong> ainda não tem regulador responsável
                  designado. Peça à administração para designar um antes de registrar decisões.
                </>
              ) : (
                <>
                  As solicitações da UBS <strong>{solicitacao.ubs.nome}</strong> são reguladas por{' '}
                  <strong>{nomesDosResponsaveis(solicitacao.ubs)}</strong>. Você pode acompanhá-las,
                  mas não registrar decisões.
                </>
              )}
            </span>
          </p>
        </CartaoConteudo>
      </Cartao>
    );
  }

  return (
    <>
      <Cartao>
        <CartaoCabecalho>
          <CartaoTitulo>Decisão da Regulação</CartaoTitulo>
          <CartaoDescricao>
            {concluida
              ? 'Esta solicitação já foi concluída. Para retomá-la, use a reabertura.'
              : 'Registre a análise desta solicitação.'}
          </CartaoDescricao>
        </CartaoCabecalho>

        <CartaoConteudo className="flex flex-wrap gap-2">
          {concluida ? (
            <Botao variante="contorno" onClick={() => setAcao('reabrir')}>
              <RotateCcw aria-hidden="true" />
              Reabrir solicitação
            </Botao>
          ) : (
            <>
              <Botao onClick={() => setAcao('agendar')}>
                <CalendarCheck aria-hidden="true" />
                Agendar
              </Botao>

              <Botao variante="contorno" onClick={() => setAcao('documentacao')}>
                <PackageCheck aria-hidden="true" />
                Documentação recebida
              </Botao>

              <Botao variante="contorno" onClick={() => setAcao('pendencia')}>
                <FileWarning aria-hidden="true" />
                Registrar pendência
              </Botao>

              {/* Só na demanda reprimida: parte do estoque anterior ao KAVI já
                  tinha atendimento marcado, e sem esta saída o pedido ficaria
                  para sempre numa fila de espera que não existe. */}
              {ehDemandaReprimida(solicitacao.origem) && (
                <Botao variante="contorno" onClick={() => setAcao('anterior')}>
                  <History aria-hidden="true" />
                  Já agendada antes do KAVI
                </Botao>
              )}

              <Botao variante="destrutivo" onClick={() => setAcao('devolver')}>
                <Undo2 aria-hidden="true" />
                Devolver
              </Botao>
            </>
          )}
        </CartaoConteudo>
      </Cartao>

      <DialogoPendencia
        aberto={acao === 'pendencia'}
        aoFechar={() => setAcao(null)}
        solicitacaoId={solicitacao.id}
        aoConcluir={invalidar}
      />
      <DialogoDocumentacao
        aberto={acao === 'documentacao'}
        aoFechar={() => setAcao(null)}
        solicitacaoId={solicitacao.id}
        aoConcluir={invalidar}
      />
      <DialogoDevolver
        aberto={acao === 'devolver'}
        aoFechar={() => setAcao(null)}
        solicitacaoId={solicitacao.id}
        aoConcluir={invalidar}
      />
      <DialogoAgendar
        aberto={acao === 'agendar'}
        aoFechar={() => setAcao(null)}
        solicitacaoId={solicitacao.id}
        aoConcluir={invalidar}
        adaptadorSimulado={situacaoEsus?.adaptador === 'simulado'}
      />
      <DialogoAgendadaAntes
        aberto={acao === 'anterior'}
        aoFechar={() => setAcao(null)}
        solicitacaoId={solicitacao.id}
        aoConcluir={invalidar}
      />
      <DialogoReabrir
        aberto={acao === 'reabrir'}
        aoFechar={() => setAcao(null)}
        solicitacaoId={solicitacao.id}
        statusAtual={solicitacao.status}
        aoConcluir={invalidar}
      />
    </>
  );
}

// ---------------------------------------------------------------------------

interface DialogoBaseProps {
  aberto: boolean;
  aoFechar: () => void;
  solicitacaoId: string;
  aoConcluir: () => Promise<void>;
}

/** Hook comum às ações: envia, avisa, atualiza e fecha. */
function useAcao(
  caminho: string,
  mensagemSucesso: string,
  { aoConcluir, aoFechar }: Pick<DialogoBaseProps, 'aoConcluir' | 'aoFechar'>,
) {
  return useMutation({
    mutationFn: (corpo: Record<string, unknown>) => api.post(caminho, corpo),
    onSuccess: async () => {
      toast.success(mensagemSucesso);
      await aoConcluir();
      aoFechar();
    },
    onError: (erro) => {
      toast.error(
        erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível registrar a decisão.',
      );
    },
  });
}

function DialogoPendencia({ aberto, aoFechar, solicitacaoId, aoConcluir }: DialogoBaseProps) {
  const [descricao, setDescricao] = React.useState('');
  const registrar = useAcao(
    `/solicitacoes/${solicitacaoId}/registrar-pendencia`,
    'Pendência registrada. A UBS já consegue ver o que falta.',
    { aoConcluir, aoFechar },
  );

  const curto = descricao.trim().length > 0 && descricao.trim().length < 10;

  return (
    <Dialogo open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogoConteudo>
        <DialogoCabecalho>
          <DialogoTitulo>Registrar pendência de documentação</DialogoTitulo>
          <DialogoDescricao>
            Descreva qual documento físico falta chegar à Regulação. O texto fica visível para a UBS
            de origem.
          </DialogoDescricao>
        </DialogoCabecalho>

        <DialogoCorpo>
          <Campo
            id="descricao-pendencia"
            rotulo="Documentação pendente"
            obrigatorio
            erro={curto ? 'Descreva com pelo menos 10 caracteres.' : undefined}
          >
            <AreaTexto
              rows={4}
              autoFocus
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Ex.: Falta o encaminhamento assinado pelo médico solicitante."
            />
          </Campo>

          <p className="text-silenciado-texto text-xs">
            Anexar arquivo no sistema não muda o status: quando o papel chegar à Regulação, use
            &quot;Documentação recebida&quot;.
          </p>
        </DialogoCorpo>

        <DialogoRodape>
          <Botao variante="contorno" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => registrar.mutate({ descricaoPendencia: descricao.trim() })}
            disabled={registrar.isPending || descricao.trim().length < 10}
          >
            {registrar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
            Registrar pendência
          </Botao>
        </DialogoRodape>
      </DialogoConteudo>
    </Dialogo>
  );
}

function DialogoDocumentacao({ aberto, aoFechar, solicitacaoId, aoConcluir }: DialogoBaseProps) {
  const [observacao, setObservacao] = React.useState('');
  const registrar = useAcao(
    `/solicitacoes/${solicitacaoId}/documentacao-recebida`,
    'Documentação registrada. A solicitação está "Em processo".',
    { aoConcluir, aoFechar },
  );

  return (
    <Dialogo open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogoConteudo>
        <DialogoCabecalho>
          <DialogoTitulo>Documentação física recebida</DialogoTitulo>
          <DialogoDescricao>
            Confirma que o documento em papel chegou à Regulação. A solicitação passa para &quot;Em
            processo&quot;.
          </DialogoDescricao>
        </DialogoCabecalho>

        <DialogoCorpo>
          <Campo id="observacao-documentacao" rotulo="Observação" ajuda="Opcional.">
            <AreaTexto
              rows={3}
              autoFocus
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Ex.: Entregue no balcão pela própria paciente."
            />
          </Campo>
        </DialogoCorpo>

        <DialogoRodape>
          <Botao variante="contorno" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => registrar.mutate({ observacao: observacao.trim() || undefined })}
            disabled={registrar.isPending}
          >
            {registrar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
            Confirmar recebimento
          </Botao>
        </DialogoRodape>
      </DialogoConteudo>
    </Dialogo>
  );
}

function DialogoDevolver({ aberto, aoFechar, solicitacaoId, aoConcluir }: DialogoBaseProps) {
  const [motivo, setMotivo] = React.useState('');
  const devolver = useAcao(
    `/solicitacoes/${solicitacaoId}/devolver`,
    'Solicitação devolvida à UBS.',
    { aoConcluir, aoFechar },
  );

  return (
    <Dialogo open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogoConteudo>
        <DialogoCabecalho>
          <DialogoTitulo>Devolver à UBS</DialogoTitulo>
          <DialogoDescricao>
            O motivo é obrigatório e fica visível para a unidade de origem — é por ele que a UBS
            saberá o que corrigir.
          </DialogoDescricao>
        </DialogoCabecalho>

        <DialogoCorpo>
          <Campo id="motivo-devolucao" rotulo="Motivo da devolução" obrigatorio>
            <AreaTexto
              rows={4}
              autoFocus
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex.: Paciente não pertence à área de abrangência desta unidade."
            />
          </Campo>
        </DialogoCorpo>

        <DialogoRodape>
          <Botao variante="contorno" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="destrutivo"
            onClick={() => devolver.mutate({ motivoDevolucao: motivo.trim() })}
            disabled={devolver.isPending || motivo.trim().length < 10}
          >
            {devolver.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
            Devolver
          </Botao>
        </DialogoRodape>
      </DialogoConteudo>
    </Dialogo>
  );
}

interface FormularioManual {
  localId: string | null;
  procedimentoId: string | null;
  profissionalId: string | null;
  dataAtendimento: string;
  horaAtendimento: string;
}

const MANUAL_VAZIO: FormularioManual = {
  localId: null,
  procedimentoId: null,
  profissionalId: null,
  dataAtendimento: '',
  horaAtendimento: '',
};

/**
 * Cadastros que alimentam os menus do preenchimento manual.
 *
 * Buscados só quando o caminho manual aparece: em atendimento normal, com o
 * eSUS respondendo, essas três listas nunca são necessárias.
 */
function useCadastrosAgendamento(habilitado: boolean) {
  const locais = useQuery({
    queryKey: ['locais-ativos'],
    queryFn: () => api.get<LocalAtivo[]>('/locais/ativos'),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });

  const procedimentos = useQuery({
    queryKey: ['procedimentos-ativos'],
    queryFn: () => api.get<ProcedimentoAtivo[]>('/procedimentos/ativos'),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });

  const profissionais = useQuery({
    queryKey: ['profissionais-ativos'],
    queryFn: () => api.get<ProfissionalAtivo[]>('/profissionais/ativos'),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });

  return {
    carregando: locais.isFetching || procedimentos.isFetching || profissionais.isFetching,
    locais: (locais.data ?? []).map((local) => ({
      valor: local.id,
      rotulo: local.nome,
      detalhe: local.endereco,
    })),
    procedimentos: (procedimentos.data ?? []).map((procedimento) => ({
      valor: procedimento.id,
      rotulo: procedimento.nome,
      detalhe: ROTULOS_TIPO[procedimento.tipo],
    })),
    profissionais: (profissionais.data ?? []).map((profissional) => ({
      valor: profissional.id,
      rotulo: profissional.nome,
      detalhe: [profissional.especialidade?.nome, profissional.conselho]
        .filter(Boolean)
        .join(' — '),
    })),
  };
}

function DialogoAgendar({
  aberto,
  aoFechar,
  solicitacaoId,
  aoConcluir,
  adaptadorSimulado,
}: DialogoBaseProps & { adaptadorSimulado: boolean }) {
  const [id, setId] = React.useState('');
  const [observacao, setObservacao] = React.useState('');
  const [consulta, setConsulta] = React.useState<RespostaConsultaEsus | null>(null);
  const [manual, setManual] = React.useState<FormularioManual>(MANUAL_VAZIO);

  React.useEffect(() => {
    if (!aberto) {
      setId('');
      setObservacao('');
      setConsulta(null);
      setManual(MANUAL_VAZIO);
    }
  }, [aberto]);

  const consultar = useMutation({
    mutationFn: () =>
      api.post<RespostaConsultaEsus>(`/solicitacoes/${solicitacaoId}/consultar-esus`, {
        idEsusRegulacao: id.trim(),
      }),
    onSuccess: (resposta) => {
      setConsulta(resposta);
      // O backend não devolve erro HTTP quando o eSUS não responde: a falha vem
      // descrita no corpo, e é ela que abre o caminho manual.
      if (resposta.falha) toast.error(resposta.falha.mensagem);
    },
    onError: (erro) => {
      setConsulta(null);
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Falha ao consultar o eSUS.');
    },
  });

  const agendar = useAcao(`/solicitacoes/${solicitacaoId}/agendar`, 'Agendamento confirmado.', {
    aoConcluir,
    aoFechar,
  });

  const agendarManual = useAcao(
    `/solicitacoes/${solicitacaoId}/agendar-manual`,
    'Agendamento registrado com os dados informados.',
    { aoConcluir, aoFechar },
  );

  const ag = consulta?.agendamento;

  // O caminho manual só existe depois de a consulta falhar e com a liberação
  // da administração — as duas condições vêm decididas do backend.
  const modoManual = Boolean(consulta?.falha && consulta.manualLiberado);

  const cadastros = useCadastrosAgendamento(modoManual);

  const manualCompleto = Boolean(
    manual.localId && manual.procedimentoId && manual.profissionalId && manual.dataAtendimento,
  );

  return (
    <Dialogo open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogoConteudo className="max-w-2xl">
        <DialogoCabecalho>
          <DialogoTitulo>Confirmar agendamento</DialogoTitulo>
          <DialogoDescricao>
            Informe o ID da solicitação no eSUS Regulação. Os dados são buscados na integração e
            conferidos antes de confirmar.
          </DialogoDescricao>
        </DialogoCabecalho>

        <DialogoCorpo>
          {adaptadorSimulado && (
            <p className="border-status-pendencia-borda bg-status-pendencia-fundo text-status-pendencia-texto flex items-start gap-2 rounded-md border p-3 text-xs">
              <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />A integração
              oficial com o eSUS está desativada. Os dados abaixo vêm de um adaptador simulado e não
              representam um agendamento real.
            </p>
          )}

          <div className="flex items-end gap-2">
            <Campo id="id-esus" rotulo="ID eSUS Regulação" obrigatorio className="flex-1">
              <Entrada
                autoFocus
                inputMode="numeric"
                value={id}
                onChange={(e) => {
                  setId(e.target.value.replace(/\D/g, ''));
                  setConsulta(null);
                }}
                placeholder="10002122285"
              />
            </Campo>
            <Botao
              variante="contorno"
              onClick={() => consultar.mutate()}
              disabled={id.trim().length < 4 || consultar.isPending}
            >
              {consultar.isPending ? (
                <Loader2 className="animate-spin" aria-hidden="true" />
              ) : (
                <Search aria-hidden="true" />
              )}
              Consultar
            </Botao>
          </div>

          {consulta?.falha && (
            <div
              role="alert"
              className="border-status-pendencia-borda bg-status-pendencia-fundo text-status-pendencia-texto flex items-start gap-2 rounded-md border p-3 text-sm"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>
                <strong className="font-medium">
                  Não foi possível resgatar os dados do eSUS Regulação.
                </strong>{' '}
                {consulta.falha.mensagem}
                {consulta.manualLiberado ? (
                  <> Preencha os dados do agendamento abaixo para seguir.</>
                ) : (
                  <>
                    {' '}
                    O preenchimento manual não está liberado pela administração, então o agendamento
                    precisa esperar a integração voltar.
                  </>
                )}
              </span>
            </div>
          )}

          {modoManual && (
            <div className="border-borda flex flex-col gap-4 rounded-lg border p-3">
              <p className="text-silenciado-texto text-xs font-semibold uppercase">
                Preenchimento manual
              </p>

              <Campo id="manual-local" rotulo="Local de atendimento" obrigatorio>
                <Combobox
                  opcoes={cadastros.locais}
                  valor={manual.localId}
                  aoEscolher={(valor) => setManual((atual) => ({ ...atual, localId: valor }))}
                  carregando={cadastros.carregando}
                  placeholder="Digite parte do nome do local"
                  semResultado="Nenhum local cadastrado com esse nome."
                />
              </Campo>

              <Campo id="manual-procedimento" rotulo="Procedimento" obrigatorio>
                <Combobox
                  opcoes={cadastros.procedimentos}
                  valor={manual.procedimentoId}
                  aoEscolher={(valor) =>
                    setManual((atual) => ({ ...atual, procedimentoId: valor }))
                  }
                  carregando={cadastros.carregando}
                  placeholder="Digite parte do nome do procedimento"
                  semResultado="Nenhum procedimento cadastrado com esse nome."
                />
              </Campo>

              <Campo id="manual-profissional" rotulo="Profissional" obrigatorio>
                <Combobox
                  opcoes={cadastros.profissionais}
                  valor={manual.profissionalId}
                  aoEscolher={(valor) =>
                    setManual((atual) => ({ ...atual, profissionalId: valor }))
                  }
                  carregando={cadastros.carregando}
                  placeholder="Digite parte do nome do profissional"
                  semResultado="Nenhum profissional cadastrado com esse nome."
                />
              </Campo>

              <div className="grid gap-4 sm:grid-cols-2">
                <Campo id="manual-data" rotulo="Data do atendimento" obrigatorio>
                  <Entrada
                    type="date"
                    value={manual.dataAtendimento}
                    onChange={(e) =>
                      setManual((atual) => ({ ...atual, dataAtendimento: e.target.value }))
                    }
                  />
                </Campo>

                <Campo id="manual-hora" rotulo="Horário" ajuda="Opcional.">
                  <Entrada
                    type="time"
                    value={manual.horaAtendimento}
                    onChange={(e) =>
                      setManual((atual) => ({ ...atual, horaAtendimento: e.target.value }))
                    }
                  />
                </Campo>
              </div>

              <Campo id="manual-observacao" rotulo="Observação" ajuda="Opcional.">
                <AreaTexto
                  rows={2}
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                />
              </Campo>

              <p className="text-silenciado-texto text-xs">
                Local, procedimento e profissional vêm dos cadastros da administração. O agendamento
                fica registrado como <strong>preenchido manualmente</strong>, com o motivo da falha,
                e o eSUS é consultado uma última vez ao gravar — se voltar a responder, os dados
                oficiais prevalecem.
              </p>
            </div>
          )}

          {ag && (
            <div className="flex flex-col gap-4">
              <div className="border-borda rounded-lg border p-3">
                <p className="text-silenciado-texto mb-2 text-xs font-semibold uppercase">
                  Agendamento no eSUS
                </p>
                <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                  <Dado rotulo="Situação" valor={ag.situacao} />
                  <Dado rotulo="Local" valor={ag.localAtendimento} />
                  <Dado
                    rotulo="Data"
                    valor={ag.dataAtendimento ? formatarData(ag.dataAtendimento) : null}
                  />
                  <Dado rotulo="Horário" valor={ag.horaAtendimento} />
                  <Dado rotulo="Profissional" valor={ag.profissionalResponsavel} />
                  <Dado rotulo="Procedimento" valor={ag.especialidadeProfissional} />
                  <Dado rotulo="Endereço" valor={ag.enderecoLocal} larguraTotal />
                </dl>
              </div>

              {!consulta.situacaoValida && (
                <p
                  role="alert"
                  className="border-destrutivo/30 bg-destrutivo/10 text-destrutivo flex items-start gap-2 rounded-md border p-3 text-sm"
                >
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />A solicitação
                  está como &quot;{ag.situacao}&quot; no eSUS e não pode ser confirmada como
                  agendada aqui.
                </p>
              )}

              {consulta.conferencia && <ConferenciaEsus conferencia={consulta.conferencia} />}

              <Campo id="observacao-agendamento" rotulo="Observação" ajuda="Opcional.">
                <AreaTexto
                  rows={2}
                  value={observacao}
                  onChange={(e) => setObservacao(e.target.value)}
                />
              </Campo>
            </div>
          )}
        </DialogoCorpo>

        <DialogoRodape>
          <Botao variante="contorno" onClick={aoFechar}>
            Cancelar
          </Botao>

          {modoManual ? (
            <Botao
              onClick={() =>
                agendarManual.mutate({
                  idEsusRegulacao: id.trim(),
                  localId: manual.localId,
                  procedimentoId: manual.procedimentoId,
                  profissionalId: manual.profissionalId,
                  dataAtendimento: manual.dataAtendimento,
                  horaAtendimento: manual.horaAtendimento || undefined,
                  observacao: observacao.trim() || undefined,
                })
              }
              disabled={!manualCompleto || agendarManual.isPending}
            >
              {agendarManual.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
              Registrar agendamento manual
            </Botao>
          ) : (
            <Botao
              onClick={() =>
                agendar.mutate({
                  idEsusRegulacao: id.trim(),
                  observacao: observacao.trim() || undefined,
                  // A confirmação explícita da divergência é registrada no
                  // histórico e na auditoria pelo backend.
                  confirmarDivergencia: consulta?.conferencia
                    ? !consulta.conferencia.tudoConfere
                    : undefined,
                })
              }
              disabled={!consulta?.agendamento || !consulta.situacaoValida || agendar.isPending}
            >
              {agendar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
              {consulta?.conferencia && !consulta.conferencia.tudoConfere
                ? 'Confirmar mesmo com divergência'
                : 'Confirmar agendamento'}
            </Botao>
          )}
        </DialogoRodape>
      </DialogoConteudo>
    </Dialogo>
  );
}

/**
 * Declara que o atendimento já estava marcado antes do KAVI.
 *
 * Só justificativa: não há campo de data, local ou profissional porque o
 * sistema não passa a alegar conhecer uma agenda que não conferiu. E não sai
 * notificação — o atendimento pode já ter acontecido, e o aviso mandaria o
 * paciente a uma consulta antiga.
 */
function DialogoAgendadaAntes({ aberto, aoFechar, solicitacaoId, aoConcluir }: DialogoBaseProps) {
  const [justificativa, setJustificativa] = React.useState('');
  const declarar = useAcao(
    `/solicitacoes/${solicitacaoId}/agendar-anterior`,
    'Demanda marcada como já agendada antes do KAVI.',
    { aoConcluir, aoFechar },
  );

  return (
    <Dialogo open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogoConteudo>
        <DialogoCabecalho>
          <DialogoTitulo>Já agendada antes do KAVI</DialogoTitulo>
          <DialogoDescricao>
            Registra que o atendimento já estava marcado quando este pedido foi cadastrado. A
            solicitação passa a &quot;Agendado&quot; e sai da fila.{' '}
            <strong>Nenhum aviso é enviado ao paciente</strong>: o atendimento foi marcado fora
            daqui e pode já ter acontecido.
          </DialogoDescricao>
        </DialogoCabecalho>

        <DialogoCorpo>
          <Campo
            id="justificativa-agendada-antes"
            rotulo="Justificativa"
            obrigatorio
            ajuda="Onde e quando o atendimento foi marcado, e como isso foi verificado."
          >
            <AreaTexto
              rows={4}
              autoFocus
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              placeholder="Ex.: Consulta marcada na central em 04/2025, conforme planilha da regulação anterior."
            />
          </Campo>
        </DialogoCorpo>

        <DialogoRodape>
          <Botao variante="contorno" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => declarar.mutate({ justificativa: justificativa.trim() })}
            disabled={declarar.isPending || justificativa.trim().length < 10}
          >
            {declarar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
            Marcar como já agendada
          </Botao>
        </DialogoRodape>
      </DialogoConteudo>
    </Dialogo>
  );
}

function DialogoReabrir({
  aberto,
  aoFechar,
  solicitacaoId,
  statusAtual,
  aoConcluir,
}: DialogoBaseProps & { statusAtual: StatusSolicitacao }) {
  const [justificativa, setJustificativa] = React.useState('');
  const reabrir = useAcao(
    `/solicitacoes/${solicitacaoId}/reabrir`,
    'Solicitação reaberta e devolvida ao fluxo.',
    { aoConcluir, aoFechar },
  );

  return (
    <Dialogo open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogoConteudo>
        <DialogoCabecalho>
          <DialogoTitulo>Reabrir solicitação</DialogoTitulo>
          <DialogoDescricao>
            Reverte a decisão de {statusAtual === 'AGENDADO' ? 'agendamento' : 'devolução'} e
            devolve a solicitação para &quot;Lido pela Regulação&quot;. A decisão revertida
            permanece no histórico.
          </DialogoDescricao>
        </DialogoCabecalho>

        <DialogoCorpo>
          <Campo id="justificativa-reabertura" rotulo="Justificativa" obrigatorio>
            <AreaTexto
              rows={4}
              autoFocus
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              placeholder="Ex.: Agendamento registrado com o ID do eSUS errado."
            />
          </Campo>
        </DialogoCorpo>

        <DialogoRodape>
          <Botao variante="contorno" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => reabrir.mutate({ justificativa: justificativa.trim() })}
            disabled={reabrir.isPending || justificativa.trim().length < 10}
          >
            {reabrir.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
            Reabrir
          </Botao>
        </DialogoRodape>
      </DialogoConteudo>
    </Dialogo>
  );
}

function Dado({
  rotulo,
  valor,
  larguraTotal,
}: {
  rotulo: string;
  valor?: string | null;
  larguraTotal?: boolean;
}) {
  return (
    <div className={larguraTotal ? 'sm:col-span-2' : undefined}>
      <dt className="text-silenciado-texto text-xs">{rotulo}</dt>
      <dd className="text-sm">{valor?.trim() ? valor : '—'}</dd>
    </div>
  );
}
